/**
 * Core Logger class providing structured, namespace-scoped logging with
 * pluggable transports, breadcrumb trails, timers, and table output.
 * @module Logger
 */

import { addBreadcrumb, getBreadcrumbs } from "./breadcrumbs";
import { getConfig } from "./config";
import { pushHistory } from "./history";
import { isNamespaceEnabled } from "./namespaceFilter";
import { ConsoleTransport } from "./transports/ConsoleTransport";
import { LogLevel, type LogEntry } from "./types";

/**
 * Converts an arbitrary value to a human-readable string for log messages.
 * Handles strings, nulls, Errors, functions, symbols, and objects (via JSON).
 *
 * @param value - Any value to stringify.
 * @returns A readable string representation.
 */
function toReadableString(value: unknown): string {
  if (typeof value === "string") return value;
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (value instanceof Error) return value.message;
  if (typeof value === "function") return `[Function: ${value.name || "anonymous"}]`;
  if (typeof value === "symbol") return value.toString();
  if (typeof value === "object") {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return Object.prototype.toString.call(value);
    }
  }
  return String(value);
}

/**
 * Structured logger scoped to a namespace (and optional file name).
 * Supports debug/info/warn/error levels, grouped output, child loggers,
 * performance timers, tabular data, and assertion checking.
 */
export class Logger {
  readonly namespace: string;
  readonly fileName?: string;
  private timers = new Map<string, number>();

  /**
   * @param namespace - Dot/colon-delimited namespace for filtering and display.
   * @param fileName - Optional source file name shown in log badges.
   */
  constructor(namespace: string, fileName?: string) {
    this.namespace = namespace;
    this.fileName = fileName;
  }

  /**
   * Logs a DEBUG-level message.
   * @param message - Log message.
   * @param data - Optional structured data to attach.
   */
  debug(message: string, data?: unknown): void {
    this.dispatch(LogLevel.DEBUG, message, data);
  }

  /**
   * Logs an INFO-level message.
   * @param message - Log message.
   * @param data - Optional structured data to attach.
   */
  info(message: string, data?: unknown): void {
    this.dispatch(LogLevel.INFO, message, data);
  }

  /**
   * Logs a WARN-level message.
   * @param message - Log message.
   * @param data - Optional structured data to attach.
   */
  warn(message: string, data?: unknown): void {
    this.dispatch(LogLevel.WARN, message, data);
  }

  /**
   * Logs an ERROR-level message with optional error object or data.
   * Attaches breadcrumb trail for debugging. Accepts non-string first
   * args gracefully (e.g. Error objects passed as message).
   *
   * @param message - Log message (or Error/object that will be coerced).
   * @param dataOrError - Optional Error instance or structured data.
   */
  error(message: string, dataOrError?: unknown): void {
    const raw: unknown = message;
    let resolvedMessage: string;
    let resolvedDataOrError = dataOrError;

    if (typeof raw !== "string") {
      if (raw instanceof Error) {
        resolvedDataOrError = raw;
        resolvedMessage = raw.message;
      } else {
        if (dataOrError === undefined) {
          resolvedDataOrError = raw;
        }
        resolvedMessage = toReadableString(raw);
      }
    } else {
      resolvedMessage = raw;
    }

    const errorValue = resolvedDataOrError instanceof Error ? resolvedDataOrError : undefined;
    const dataValue = resolvedDataOrError instanceof Error ? undefined : resolvedDataOrError;
    const entry: LogEntry = {
      level: LogLevel.ERROR,
      namespace: this.namespace,
      fileName: this.fileName,
      message: resolvedMessage,
      data: dataValue,
      error: errorValue,
      timestamp: new Date(),
      breadcrumbs: getBreadcrumbs(),
    };

    if (!this.shouldLog(LogLevel.ERROR)) return;

    pushHistory(entry);

    const config = getConfig();
    for (const transport of config.transports) {
      transport.log(entry);
    }
  }

  /**
   * Logs a collapsed console group containing the output of `fn`.
   *
   * @param label - Group label shown in the console.
   * @param fn - Callback whose console output is captured inside the group.
   */
  group(label: string, fn: () => void): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;

    console.groupCollapsed(
      `%c${this.namespace}%c ${label}`,
      "background:#6C5CE7;color:#fff;padding:2px 6px;border-radius:3px;font-weight:600;",
      "color:inherit;font-weight:normal;",
    );
    try {
      fn();
    } finally {
      console.groupEnd();
    }
  }

  /**
   * Creates a child logger with a sub-namespace appended (e.g. "App:Auth").
   *
   * @param subNamespace - Namespace segment to append.
   * @param fileName - Optional file name override; inherits parent's if omitted.
   * @returns A new Logger instance with the combined namespace.
   */
  child(subNamespace: string, fileName?: string): Logger {
    const childNs = `${this.namespace}:${subNamespace}`;
    return new Logger(childNs, fileName ?? this.fileName);
  }

  /**
   * Starts a named performance timer.
   * @param label - Timer identifier (must match the corresponding `timeEnd` call).
   */
  time(label: string): void {
    this.timers.set(label, performance.now());
  }

  /**
   * Stops the named timer and logs the elapsed duration in milliseconds.
   * Warns if the timer does not exist.
   *
   * @param label - Timer identifier previously passed to `time()`.
   */
  timeEnd(label: string): void {
    const start = this.timers.get(label);
    if (start === undefined) {
      this.warn(`Timer "${label}" does not exist`);
      return;
    }
    this.timers.delete(label);
    const elapsed = performance.now() - start;

    if (!this.shouldLog(LogLevel.INFO)) return;

    const config = getConfig();
    for (const transport of config.transports) {
      if (transport instanceof ConsoleTransport) {
        transport.logTimer(this.namespace, this.fileName, label, elapsed);
      } else {
        transport.log({
          level: LogLevel.INFO,
          namespace: this.namespace,
          fileName: this.fileName,
          message: `⏱️ ${label}: ${elapsed.toFixed(2)}ms`,
          timestamp: new Date(),
        });
      }
    }

    pushHistory({
      level: LogLevel.INFO,
      namespace: this.namespace,
      fileName: this.fileName,
      message: `⏱️ ${label}: ${elapsed.toFixed(2)}ms`,
      timestamp: new Date(),
    });
  }

  /**
   * Outputs tabular data via transports that support it (falls back to standard log).
   *
   * @param data - Array of row objects to display.
   * @param columns - Optional subset of columns to show.
   */
  table(data: unknown[], columns?: string[]): void {
    if (!this.shouldLog(LogLevel.INFO)) return;

    const config = getConfig();
    for (const transport of config.transports) {
      if (transport.table) {
        transport.table(data, columns);
      } else {
        transport.log({
          level: LogLevel.INFO,
          namespace: this.namespace,
          fileName: this.fileName,
          message: "Table output",
          data,
          timestamp: new Date(),
        });
      }
    }
  }

  /**
   * Logs an error if the condition is falsy (assertion-style guard).
   *
   * @param condition - Value to assert as truthy.
   * @param message - Message logged on assertion failure.
   * @param data - Optional data attached to the error log.
   */
  assert(condition: unknown, message: string, data?: unknown): void {
    if (!condition) {
      this.error(`Assertion failed: ${message}`, data);
    }
  }

  private shouldLog(level: LogLevel): boolean {
    const config = getConfig();
    if (!config.enabled) return false;
    if (level < config.level) return false;
    if (!isNamespaceEnabled(this.namespace)) return false;
    return true;
  }

  private dispatch(level: LogLevel, message: string, data?: unknown): void {
    if (!this.shouldLog(level)) return;

    const raw: unknown = message;
    let resolvedMessage: string;
    let resolvedData = data;

    if (typeof raw !== "string") {
      if (data === undefined) {
        resolvedData = raw;
      }
      resolvedMessage = toReadableString(raw);
    } else {
      resolvedMessage = raw;
    }

    const entry: LogEntry = {
      level,
      namespace: this.namespace,
      fileName: this.fileName,
      message: resolvedMessage,
      data: resolvedData,
      timestamp: new Date(),
    };

    addBreadcrumb(entry);
    pushHistory(entry);

    const config = getConfig();
    for (const transport of config.transports) {
      transport.log(entry);
    }
  }
}
